// AltShop UK — stitches the GA4 client ID onto the cart, so a completed order
// carries it through as note_attributes.ga_client_id.
//
// WHY: GA4's client-side `purchase` event only fires if the buyer's browser
// reaches and holds the Thank You page — it structurally undercounts (see
// docs/analytics-tracking.md §5.2, TODO.md #19). The fix is a server-side
// webhook that re-sends the purchase to GA4 via Measurement Protocol once the
// order exists — but that needs the visitor's GA4 client ID, and by the time
// the order webhook fires, the browser session is long gone. This script's
// only job is to capture that client ID onto the cart *before* checkout, so
// it's still there once the order webhook runs.
//
// NOT part of the web pixel (docs/shopify-web-pixel-ga4.js) — a Web Pixel can
// observe events and send data out, but has no API to write back to the cart.
// This has to be ordinary theme JavaScript, running in the top frame.
//
// SOURCE OF TRUTH mirrors the live copy at Theme → Assets →
// ga-client-id-attribute.js, included from layout/theme.liquid. Keep in sync.
//
// CONSENT: uses window.Shopify.customerPrivacy — the correct API for
// top-frame theme code (unlike the pixel, which must use init.customerPrivacy
// — see docs/analytics-tracking.md §6 for why those two are not
// interchangeable). Fails closed: if consent state is unknown or declined,
// this does nothing. The _ga cookie will not exist yet anyway in that case,
// since Shopify withholds the Google & YouTube channel's tag (the thing that
// actually sets _ga) until consent is granted.
//
// IMPORTANT, confirmed empirically 23/08/2026 (Playwright, live preview
// theme): window.Shopify.customerPrivacy exists as soon as the banner has
// rendered, but by default it's a bare data object ({cachedConsent,
// consentStatus}) with NO methods — analyticsProcessingAllowed is undefined
// on it. The full method surface only appears after explicitly loading it via
// Shopify.loadFeatures([{name:'consent-tracking-api', version:'0.1'}], cb) —
// see https://shopify.dev/docs/api/customer-privacy. Skipping this step is a
// silent failure: the check always returns false and the script never fires,
// with no error anywhere. Do not remove the loadFeatures call.
(function () {
  'use strict';

  function readGaClientId() {
    // gtag's _ga cookie: GA1.1.<id1>.<id2> — Measurement Protocol's client_id
    // is the "<id1>.<id2>" tail, not the GA1.1 prefix.
    var match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!match) return null;
    var parts = decodeURIComponent(match[1]).split('.');
    if (parts.length < 4) return null;
    return parts[2] + '.' + parts[3];
  }

  function alreadySent(clientId) {
    try {
      return sessionStorage.getItem('altshop_ga_client_id_sent') === clientId;
    } catch (e) {
      return false; // storage unavailable (private mode etc.) — just send again, harmless
    }
  }

  function markSent(clientId) {
    try {
      sessionStorage.setItem('altshop_ga_client_id_sent', clientId);
    } catch (e) {
      // ignore — nothing to clean up
    }
  }

  function sendToCart(clientId) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { ga_client_id: clientId } }),
    })
      .then(function (res) {
        if (res.ok) markSent(clientId);
      })
      .catch(function () {
        // network hiccup — next page view retries, nothing to do here
      });
  }

  function attempt() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return;
    var allowed;
    try {
      allowed = cp.analyticsProcessingAllowed();
    } catch (e) {
      return;
    }
    if (!allowed) return;
    var clientId = readGaClientId();
    if (!clientId || alreadySent(clientId)) return;
    sendToCart(clientId);
  }

  // Poll for up to ~10s: covers both the Google & YouTube channel's tag
  // (which sets _ga) loading asynchronously, and a visitor who accepts the
  // consent banner partway through this pageview — the next tick picks up
  // the change. A visitor who takes longer than that is caught on their next
  // page load instead, which is fine given normal multi-page browsing before
  // checkout.
  function startPolling() {
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      attempt();
      if (tries >= 20) clearInterval(poll);
    }, 500);
  }

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error) return; // feature failed to load — nothing safe to do
        startPolling();
      }
    );
  }
})();
